<template>
  <section class="est-room" :class="{ 'est-room--page': isGuestPage, 'is-live': isLive }">
    <!-- Üst şerit yalnız danışan sayfasında: kabuksuz sayfanın kimliği (Vega
         logosu) ve bağlantı durumu, görüşmede süre. Host Vega kabuğunun içindedir;
         orada ikinci bir başlık şeridi gereksizdi, bilgi kartı aynı bilgiyi taşır. -->
    <header v-if="isGuestPage" class="est-room__bar">
      <img class="est-room__logo" :src="brandLogo" alt="Vega">
      <div v-if="!isMeetingEnded && !loadFailed" class="est-room__meta">
        <span v-if="isLive" class="est-room__timer" :title="$t('OnlineMeetingComponent.CallDuration')">
          <font-awesome-icon icon="fa-solid fa-clock" /> {{ elapsedLabel }}
        </span>
        <span class="est-room__pill" :class="connectionState.tone">
          <span class="est-room__pill-dot" aria-hidden="true"></span>{{ $t(connectionState.label) }}
        </span>
      </div>
    </header>

    <div v-if="isMeetingEnded" class="est-room__screen">
      <div class="est-room__card est-room__screen-card">
        <span class="est-room__screen-icon" aria-hidden="true"><font-awesome-icon icon="fa-solid fa-circle-check" /></span>
        <h2 class="est-room__heading">{{ $t('OnlineMeetingComponent.MeetingCompletedTitle') }}</h2>
        <p class="est-room__lead">{{ $t(isHost ? 'OnlineMeetingComponent.HostMeetingCompleted' : 'OnlineMeetingComponent.ThanksForJoining') }}</p>
      </div>
    </div>

    <div v-else-if="loadFailed" class="est-room__screen">
      <div class="est-room__card est-room__screen-card">
        <span class="est-room__screen-icon is-danger" aria-hidden="true"><font-awesome-icon icon="fa-solid fa-triangle-exclamation" /></span>
        <h2 class="est-room__heading">{{ $t('OnlineMeetingComponent.MeetingLoadError') }}</h2>
        <p class="est-room__lead">{{ $t('OnlineMeetingComponent.MeetingLoadErrorText') }}</p>
        <button type="button" class="btn btn-primary est-room__cta" @click="loadMeeting">
          <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> {{ $t('TryAgain') }}
        </button>
      </div>
    </div>

    <div v-else class="est-room__main">
      <div class="est-room__stagecol">
        <meeting-preview-component v-show="isLandingMode"
                                   :self-name="username"
                                   :connections="connections"
                                   :media-state="mediaState"
                                   :is-camera-open="isCameraOpen"
                                   :is-microphone-open="isMicrophoneOpen"
                                   @toggle-camera="changeCameraStatus"
                                   @toggle-mic="changeMicrophoneStatus" />

        <!-- Sahne `v-show` ile durur: store'daki `stream` olayı `remotePlayer`ı
             DOM'da bulmak zorunda. -->
        <meeting-stage-component v-show="isLive"
                                 :self-name="username"
                                 :camera-on="isCameraOpen"
                                 :mic-on="isMicrophoneOpen"
                                 :disabled="!hasLocalStream"
                                 :is-ending="isEnding"
                                 @toggle-camera="changeCameraStatus"
                                 @toggle-mic="changeMicrophoneStatus"
                                 @end-meeting="endMeeting" />
      </div>

      <div class="est-room__side">
        <meeting-landing-component v-if="isLandingMode"
                                   :is-host="isHost"
                                   :meeting-url="meetingUrl"
                                   :connections="connections"
                                   :media-state="mediaState"
                                   :meeting-info="meetingInfo"
                                   :counterpart-name="counterpartName"
                                   :connection="connectionChip"
                                   :is-camera-open="isCameraOpen"
                                   :is-microphone-open="isMicrophoneOpen"
                                   :is-joining="isJoining"
                                   @retry-media="startStreaming"
                                   @start-meeting="offerP2pConnection"
                                   @join-meeting="acceptP2pConnection" />

        <template v-else>
          <meeting-right-component :meeting="meeting"
                                   :meeting-info="meetingInfo"
                                   :counterpart-name="counterpartName"
                                   :connection="connectionChip"
                                   :is-host="isHost"
                                   :show-status="!isGuestPage"
                                   :connection-state="connectionState"
                                   :elapsed-label="elapsedLabel" />

          <!-- Sohbet bilgi kartının altında, sahnenin alt hizasına kadar uzar ve her
               zaman görünür: açılıp kapanan panel mesajı kaçırtıyordu, sahnenin
               altına konması ise sayfayı iki ekran boyu yapıyordu. -->
          <div class="est-room__card est-room__chatcard">
            <p class="est-room__card-head">
              <font-awesome-icon icon="fa-solid fa-comment" /> {{ $t('OnlineMeetingComponent.Chat') }}
            </p>
            <meeting-chat-component :is-host="isHost"
                                    :messages="meeting.chatMessages"
                                    :host-name="meeting.hostName"
                                    :guest-name="meeting.guestName"
                                    :meeting-id="meeting.id" />
          </div>
        </template>
      </div>
    </div>

    <!-- Alt şerit yalnız danışan sayfasında: şifreli bağlantı notu ve sorun anında
         klinik telefonu. Kabukta Vega'nın kendi alt şeridi var. -->
    <footer v-if="isGuestPage" class="est-room__foot">
      <span class="est-room__foot-note">
        <font-awesome-icon icon="fa-solid fa-lock" />{{ $t('OnlineMeetingComponent.SecureNote') }}
      </span>
      <a v-if="clinicPhone" class="est-room__foot-note" :href="'tel:' + clinicPhoneHref">
        <font-awesome-icon icon="fa-solid fa-phone" />{{ $t('OnlineMeetingComponent.TroubleCallClinic') }}: {{ clinicPhone }}
      </a>
    </footer>
  </section>
</template>

<script>
  import 'webrtc-adapter'
  import * as signalR from '@microsoft/signalr'
  import { parsePhoneNumberFromString } from 'libphonenumber-js'

  import brandLogo from '../../../assets/images/logo-full-lg.png'
  import MeetingChatComponent from './MeetingChatComponent.vue'
  import MeetingLandingComponent from './MeetingLandingComponent.vue'
  import MeetingPreviewComponent from './MeetingPreviewComponent.vue'
  import MeetingRightComponent from './MeetingRightComponent.vue'
  import MeetingStageComponent from './MeetingStageComponent.vue'
  import MeetingService from '../../../services/meeting.js'
  import { playNotificationSound } from '../../../helpers/notificationSound'

  export default {
    name: 'OnlineMeetingHost',
    props: {
      appointmentId: String
    },
    components: {
      MeetingChatComponent,
      MeetingLandingComponent,
      MeetingPreviewComponent,
      MeetingRightComponent,
      MeetingStageComponent
    },
    data: function () {
      return {
        meeting: { id: "", isStarted: false, isEnded: false, chatMessages: [] },
        connections: {
          // Odaya bağlı mıyım (hub açık). Karşı tarafın odada olmasından (`SignalR`)
          // ayrı okunur: uzman gelmemişken danışan "Bağlanıyor" değil "Uzman
          // bekleniyor" görmeli.
          Hub: false,
          // Karşı taraf odada mı.
          SignalR: false,
          WebRtc: false,
          // 0: izin bekleniyor · 1: erişim var · -1: engellendi · -2: cihaz yok
          // · -3: tarayıcı/adres desteklemiyor
          LocalCamera: 0
        },
        isMeetingLoading: true,
        isMeetingEnded: false,
        loadFailed: false,
        isEnding: false,
        isJoining: false,

        isCameraOpen: true,
        isMicrophoneOpen: true,

        // Randevu fişi (`GetOnlineMeetingDetails`): uzman, hizmet, saat, şube telefonu.
        // Görüşme yüklendikten sonra ayrı gelir; gelmeden de ekran çalışır.
        meetingInfo: null,

        brandLogo: brandLogo,
        // Karşı tarafın odaya girdiği yalnız BİR kez duyurulur; yeniden bağlanmalarda
        // (mobilde sekme arka plana alındığında sık olur) ses tekrar çalmaz.
        remoteJoinAnnounced: false,

        elapsedSeconds: 0,
        timerHandle: null
      }
    },
    beforeDestroy() {
      this.stopTimer();
      this.stopLocalStream();
    },
    methods: {
      fetchMeetingInfo() {
        if (!this.meeting || !this.meeting.id) return;

        // Uç `false` da dönebiliyor (kayıt yok, hata); yalnız nesne kabul edilir.
        MeetingService.getOnlineMeetingDetails(this.meeting.id)
          .then(data => {
            this.meetingInfo = data && typeof data === 'object' ? data : null;
          })
          .catch(error => console.error(error));
      },
      startStreaming() {
        let self = this;

        // Güvenli olmayan (http) adreste ve eski tarayıcıda `mediaDevices` hiç
        // tanımlı olmaz; eski hâl burada TypeError atıp durumu "izin bekleniyor"da
        // bırakıyordu, yani ekran sonsuza kadar bekliyor görünüyordu.
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          self.connections.LocalCamera = -3;
          return;
        }

        self.connections.LocalCamera = 0;

        navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        }).then(function (stream) {
          self.connections.LocalCamera = 1;
          self.isCameraOpen = true;
          self.isMicrophoneOpen = true;
          self.$store.dispatch("Meeting/setMeetingStream", stream);
        }).catch((err) => {
          console.error(err);
          // Cihazın hiç olmaması ile iznin reddedilmesi ayrı sorunlardır: ilkinde
          // adres çubuğundaki izin simgesini aramanın faydası yok.
          const noDevice = ["NotFoundError", "DevicesNotFoundError", "NotReadableError", "TrackStartError"];
          self.connections.LocalCamera = noDevice.includes(err && err.name) ? -2 : -1;
        });
      },
      changeCameraStatus() {
        // Akış yokken `getVideoTracks()[0]` undefined'dı ve `.enabled` patlıyordu.
        const track = this.localStream && this.localStream.getVideoTracks()[0];
        if (!track) return;

        this.isCameraOpen = !this.isCameraOpen;
        track.enabled = this.isCameraOpen;
      },
      changeMicrophoneStatus() {
        const track = this.localStream && this.localStream.getAudioTracks()[0];
        if (!track) return;

        this.isMicrophoneOpen = !this.isMicrophoneOpen;
        track.enabled = this.isMicrophoneOpen;
      },
      stopLocalStream() {
        if (this.localStream) {
          this.localStream.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
          });
        }

        // Görüşme hiç başlamadan sayfadan çıkılırsa `simplepeer` null'dır; eski
        // hâl koşulsuz `destroy()` çağırıp TypeError atıyordu.
        if (this.$store.state.Meeting.simplepeer) {
          this.$store.state.Meeting.simplepeer.destroy();
        }
      },
      startTimer() {
        this.stopTimer();
        this.elapsedSeconds = 0;
        this.timerHandle = setInterval(() => {
          this.elapsedSeconds += 1;
        }, 1000);
      },
      stopTimer() {
        if (this.timerHandle) {
          clearInterval(this.timerHandle);
          this.timerHandle = null;
        }
      },
      confirmEnd() {
        return this.$swal.fire({
          icon: 'warning',
          title: this.$t("AreYouSure"),
          text: this.$t("OnlineMeetingComponent.ConfirmEndMeeting"),
          buttonsStyling: false,
          confirmButtonText: this.$t("OnlineMeetingComponent.EndMeeting"),
          confirmButtonClass: "btn btn-danger btn-sm",
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          cancelButtonClass: "btn btn-secondary btn-sm",
          reverseButtons: true
        }).then(result => result.value === true);
      },
      endMeeting() {
        this.confirmEnd().then(confirmed => {
          if (!confirmed) return;

          this.isEnding = true;

          MeetingService.endMeeting(this.meeting.id)
            .then(response => {
              if (response) {
                this.stopLocalStream();
                this.isMeetingEnded = true;
                this.$store.state.Meeting.meethub.invoke("EndMeetingToGuest");

                if (this.isHost) {
                  setTimeout(() => {
                    this.$router.push("/");
                  }, 5000);
                }
              } else {
                this.$toastr.error(this.$t("Error"));
              }
            })
            .catch(() => this.$toastr.error(this.$t("Error")))
            .finally(() => {
              this.isEnding = false;
            });
        });
      },
      offerP2pConnection() {
        this.$store.state.Meeting.isMeetingStarted = true;
        this.$store.state.Meeting.meethub.invoke("SendMeetingJoinOfferToGuest");
      },
      acceptP2pConnection() {
        this.isJoining = true;
        this.$store.state.Meeting.meethub.invoke("AcceptMeetingJoinOffer");
      },
      loadMeeting() {
        let $this = this;

        $this.loadFailed = false;
        $this.isMeetingLoading = true;

        return MeetingService.createOrGetByAppointmentId($this.isHost, $this.appointmentId)
          .then(response => {
            if (response.data && response.data.meetingNotExists == true) {
              setTimeout(function () {
                $this.stopLocalStream();
              }, 3000);
              $this.isMeetingEnded = true;
              return;
            }

            if (response.data.guestCulture) {
              $this.$bus.$emit('UiLanguageChanged', response.data.guestCulture);
            }

            $this.meeting = response.data;
            $this.fetchMeetingInfo();

            // ICE listesi peer kurulmadan ÖNCE hazır olmalı: `initializeSimplepeer`
            // senkrondur ve store'daki listeyi okur. Uç erişilemezse servis
            // reddedilir, store fallback listeye düşer.
            return MeetingService.getIceServers($this.meeting.id)
              .then(data => $this.$store.dispatch("Meeting/setIceServers", data && data.iceServers))
              .catch(() => $this.$store.dispatch("Meeting/setIceServers", null))
              .then(() => {
                let meetHubUrl = `/meetHub?meetingId=${$this.meeting.id}&isHost=${$this.isHost}&username=${$this.username}`;
                $this.$store.dispatch("Meeting/initializeMeethub", meetHubUrl);

                $this.registerHubHandlers();
              });
          })
          .catch(error => {
            console.error(error);
            $this.loadFailed = true;
          })
          .finally(() => {
            $this.isMeetingLoading = false;
          });
      },
      registerHubHandlers() {
        let $this = this;
        const meethub = $this.$store.state.Meeting.meethub;

        //Biri odaya geldi, biz de onu karşılayalım.
        meethub.on('OnRemoteConnected', function () {
          meethub.invoke("NotifyRemote");
          $this.connections.SignalR = true;

          // Odada bekleyen taraf ekrana bakmıyor olabilir. Ses tarayıcı politikası
          // gereği yalnız sayfaya daha önce dokunulmuşsa duyulur; duyulmaması
          // hiçbir akışı etkilemez.
          if (!$this.remoteJoinAnnounced) {
            $this.remoteJoinAnnounced = true;
            playNotificationSound();
          }
        });

        //Geldiğim odadaki karşı taraftan bir yanıt aldım.
        meethub.on('OnRemoteNotificationReceived', function () {
          $this.connections.SignalR = true;
        });

        meethub.on('OnDisconnect', function (connectionId, error) {
          // Eski hâl burada danışanın sayfasını `location.reload()` ile komple
          // yeniliyordu: karşı taraf bir an kopunca görüşme sıfırlanıyor, doktorun
          // yeniden başlatması gerekiyordu. Prod'da tek bir görüşmede 478 yeniden
          // katılım bu döngüden doğmuştu.
          $this.connections.SignalR = false;
          $this.$store.state.Meeting.isMeetingStarted = false;
          $this.isJoining = false;

          $this.$toastr.error($this.$t("OnlineMeetingComponent.SignalrDisconnectMessage"));

          if (error != null) {
            console.error(error);
          }

          setTimeout(function () {
            if (meethub.state === signalR.HubConnectionState.Disconnected) {
              meethub.start();
            }
          }, 5000);
        });

        meethub.onreconnecting(function () {
          $this.connections.Hub = false;
        });

        // Yeniden bağlanma denemeleri de bittiyse karşı tarafın odada olduğu
        // bilinemez; iki bayrak da düşer. `OnDisconnect` kancası hub'ı yeniden başlatır.
        meethub.onclose(function () {
          $this.connections.Hub = false;
          $this.connections.SignalR = false;
        });

        // SignalR geri geldiğinde sayfayı yenilemek yerine peer'ı yeniden
        // kurarız: sohbet geçmişi ve medya izni korunur.
        meethub.onreconnected(function () {
          $this.connections.Hub = true;
          $this.connections.SignalR = true;
          meethub.invoke("NotifyRemote");

          // Taraf ayrımı yok: host yeniden kurduğunda teklifi o üretir, danışan
          // kurduğunda gelen teklifi bekler. Yalnız host'u dışarıda bırakmak,
          // bağlantıyı host tarafında koptuğu yerde bırakıyordu.
          const peer = $this.$store.state.Meeting.simplepeer;
          if (peer && peer.destroyed) {
            $this.$store.dispatch('Meeting/initializeSimplepeer', $this.isHost);
          }
        });

        meethub.on('OnTextMessageReceived', function (incomingMessage, translateThis) {
          if (translateThis) {
            incomingMessage.senderName = $this.$t(incomingMessage.senderName)
            incomingMessage.message = $this.$t(incomingMessage.message)
          }

          $this.meeting.chatMessages.push(incomingMessage);
        });

        meethub.on('OnFileReceived', function () {
          $this.$bus.$emit('RefreshMultimediaList');
        });

        //Bu metod sadece guest için çalışacaktır.
        meethub.on('MeetingJoinOfferReceived', function () {
          $this.$store.state.Meeting.isMeetingStarted = true;
          if ($this.p2pConnectionEnabled == false) {
            $this.$store.dispatch('Meeting/initializeSimplepeer', $this.isHost);
          }
        });

        //Bu metod sadece guest için çalışacaktır.
        meethub.on('MeetingOfferEnded', function () {
          $this.stopLocalStream();
          $this.isMeetingEnded = true;
        });

        //Bu metod sadece host için çalışacaktır.
        meethub.on('MeetingJoinOfferAccepted', function () {
          //toplantının başladığı noktadır.
          if ($this.p2pConnectionEnabled == false) {
            $this.$store.dispatch('Meeting/initializeSimplepeer', $this.isHost);
          }
        });

        meethub.on('OnSignalReceived', function (incomingSignal) {
          // Sinyal geldiyse karşı taraf peer'ını kurmuş demektir. Bizde peer yoksa
          // ya da önceki denemeden ölü kaldıysa KENDİ peer'ımızı kurarız.
          //
          // Eski hâl burada `offerP2pConnection()` çağırıyordu: bu, karşı tarafa
          // yeni bir katılım daveti göndermektir. Davet karşı tarafta ikinci bir
          // peer doğuruyor, aynı oturuma iki teklif geliyor ve ikinci `signal()`
          // peer'ı yok ediyordu — ardından gelen her sinyal "cannot signal after
          // peer is destroyed" ile patlıyordu.
          let peer = $this.$store.state.Meeting.simplepeer;

          if (!peer || peer.destroyed) {
            $this.$store.dispatch('Meeting/initializeSimplepeer', $this.isHost);
            peer = $this.$store.state.Meeting.simplepeer;
          }

          if (!peer || peer.destroyed) return;

          const signal = JSON.parse(incomingSignal);

          // Trickle açıkken ICE adayları bağlantı kurulduktan sonra da gelir ve
          // peer'a verilmelidir. Yok sayılan tek şey, bağlantı kurulduktan sonra
          // gelen yinelenmiş oturum tanımıdır (offer/answer): ikincisi peer'ı
          // geçersiz duruma sokup yok ediyordu. `_connected` özel alandır ama
          // paketin tek bağlantı göstergesi odur.
          if (peer._connected && signal.type) return;

          peer.signal(signal);
        });

        meethub.start().then(() => {
          $this.connections.Hub = true;

          if ($this.isHost == true) {
            meethub.invoke("OnHostCreated", $this.appointmentId)
          } else {
            meethub.invoke("OnGuestCreated", $this.appointmentId)
          }
        })
          .catch(() => {
            setTimeout(function () {
              meethub.start();
            }, 1500);
          });
      }
    },
    computed: {
      isHost: function () {
        return this.$route.meta.isHost === true;
      },
      // Danışan rotası (`/joinToMeeting`) kabuksuz `MeetingLayout` içindedir: oda
      // kendi tam-sayfa kabuğunu basar. Host Vega kabuğunun (`Layout`) içinde kalır.
      isGuestPage: function () {
        return !this.isHost;
      },
      username: function () {
        return (this.isHost == true ? this.meeting.hostName : this.meeting.guestName);
      },
      // Karşı taraf: danışanda uzman (fişten; gelene kadar randevudaki ad), hostta danışan.
      counterpartName: function () {
        if (this.isHost) return this.meeting.guestName || '';
        return (this.meetingInfo && this.meetingInfo.staffName) || this.meeting.hostName || '';
      },
      clinicPhone: function () {
        const raw = (this.meetingInfo && this.meetingInfo.branchPhone) || '';
        if (!raw) return '';

        // Şube telefonu DB'de çoğu zaman ham (`02165177111`). Ulusal biçimde
        // yazılmışsa Türkiye varsayılır ve uluslararası biçime çevrilir;
        // ayrıştırılamayan değer olduğu gibi basılır.
        const parsed = parsePhoneNumberFromString(String(raw), 'TR');
        return parsed && parsed.isValid() ? parsed.formatInternational() : raw;
      },
      clinicPhoneHref: function () {
        return this.clinicPhone.replace(/[^\d+]/g, '');
      },
      meetingUrl: function () {
        let url = window.location.origin;
        if (url.includes("https://")) {
          return url + "/joinToMeeting/" + this.appointmentId;
        } else {
          return url.replace("http", "https") + "/joinToMeeting/" + this.appointmentId;
        }
      },
      p2pConnectionEnabled: function () {
        return this.$store.state.Meeting.simplepeer ? this.$store.state.Meeting.simplepeer._connected : false;
      },
      localStream: function () {
        return this.$store.getters['Meeting/meetingStream'];
      },
      hasLocalStream: function () {
        return this.localStream != null;
      },
      isLandingMode: function () {
        return this.$store.state.Meeting.simplepeerConnected == false;
      },
      isLive: function () {
        return !this.isLandingMode;
      },
      // Kamera durumu tek yerden okunur; sahnedeki blok başlığı, hazırlık kartı ise
      // açıklamayı ve kurtarma aksiyonunu basar (aynı cümle iki kez yazılmaz).
      mediaState: function () {
        switch (this.connections.LocalCamera) {
          case 1:
            return this.isCameraOpen
              ? { tone: 'is-ok', icon: 'fa-solid fa-circle-check', title: 'OnlineMeetingComponent.CameraPermissionSuccessTitle', text: 'OnlineMeetingComponent.CameraPermissionSuccess' }
              : { tone: 'is-ok', icon: 'fa-solid fa-video-slash', title: 'OnlineMeetingComponent.CameraClosed', text: 'OnlineMeetingComponent.CameraPermissionSuccess' };
          case -1:
            return { tone: 'is-danger', icon: 'fa-solid fa-triangle-exclamation', title: 'OnlineMeetingComponent.CameraBlocked', text: 'OnlineMeetingComponent.CameraPermissionError', canRetry: true };
          case -2:
            return { tone: 'is-danger', icon: 'fa-solid fa-video-slash', title: 'OnlineMeetingComponent.CameraNotFound', text: 'OnlineMeetingComponent.CameraNotFoundText', canRetry: true };
          case -3:
            return { tone: 'is-danger', icon: 'fa-solid fa-triangle-exclamation', title: 'OnlineMeetingComponent.CameraNotSupported', text: 'OnlineMeetingComponent.CameraNotSupportedText' };
          default:
            return { tone: 'is-wait', icon: 'fa-solid fa-hourglass-half', title: 'OnlineMeetingComponent.CameraPermissionPending', text: 'OnlineMeetingComponent.CameraPermissionInformation' };
        }
      },
      // Üst şeritteki hap: karşı tarafın varlığı. `Hub` (odaya bağlıyım) ve
      // `SignalR` (karşı taraf odada) ayrı okunur: "Bağlanıyor → Karşı taraf
      // bekleniyor → Karşı taraf odada → Bağlı".
      connectionState: function () {
        if (!this.connections.Hub) {
          return { tone: 'is-wait', label: 'OnlineMeetingComponent.StateConnecting' };
        }
        if (this.isLive) {
          // "Bağlı" tek başına yetmiyordu: görüntü donduğunda ya da ses kesildiğinde
          // gösterge yeşil kaldığı için kullanıcı sorunu karşı tarafta sanıyordu.
          // Ölçüm `getStats` üzerinden gelir (store → `connectionQuality`).
          const quality = this.$store.state.Meeting.connectionQuality;

          if (quality && quality.level === 'poor') {
            return { tone: 'is-off', label: 'OnlineMeetingComponent.StateConnectionPoor' };
          }
          if (quality && quality.level === 'fair') {
            return { tone: 'is-wait', label: 'OnlineMeetingComponent.StateConnectionFair' };
          }

          return { tone: 'is-on', label: 'OnlineMeetingComponent.StateConnected' };
        }
        if (!this.connections.SignalR) {
          return { tone: 'is-wait', label: 'OnlineMeetingComponent.StateWaitingOther' };
        }
        return { tone: 'is-on', label: 'OnlineMeetingComponent.StateOtherInRoom' };
      },
      // Fişteki bağlantı çipi: her an görünür. Hazırlıkta odaya bağlı olup olmadığını,
      // görüşmede ölçülen kaliteyi (gecikme, aralık bazlı kayıp) söyler. Üst şeritteki
      // hap karşı tarafın varlığını taşır; çip ise ağın kendisini.
      connectionChip: function () {
        if (!this.connections.Hub) {
          return { tone: 'est-chip--warn', icon: 'fa-solid fa-circle-notch', spin: true, label: 'OnlineMeetingComponent.StateConnecting', detail: '' };
        }

        if (!this.isLive) {
          return { tone: '', icon: 'fa-solid fa-signal', label: 'OnlineMeetingComponent.ConnectionReady', detail: '' };
        }

        const quality = this.$store.state.Meeting.connectionQuality;
        if (!quality) {
          return { tone: '', icon: 'fa-solid fa-signal', label: 'OnlineMeetingComponent.ConnectionMeasuring', detail: '' };
        }

        const parts = [`${Math.round(quality.rttMs)} ms`];
        if (quality.lossPercent >= 1) {
          parts.push(this.$t('OnlineMeetingComponent.VegaLossShort', { value: Math.round(quality.lossPercent) }));
        }
        const detail = parts.join(' · ');

        if (quality.level === 'poor') {
          return { tone: 'est-chip--danger', icon: 'fa-solid fa-signal-weak', label: 'OnlineMeetingComponent.StateConnectionPoor', detail };
        }
        if (quality.level === 'fair') {
          return { tone: 'est-chip--warn', icon: 'fa-solid fa-signal-fair', label: 'OnlineMeetingComponent.StateConnectionFair', detail };
        }
        return { tone: 'est-chip--ok', icon: 'fa-solid fa-signal-strong', label: 'OnlineMeetingComponent.ConnectionGood', detail };
      },
      elapsedLabel: function () {
        const total = this.elapsedSeconds;
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        const two = n => String(n).padStart(2, '0');
        return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${two(m)}:${two(s)}`;
      }
    },
    watch: {
      isLive(val) {
        if (val) {
          this.startTimer();
        } else {
          this.stopTimer();
        }
      }
    },
    mounted: function () {
      this.startStreaming();
      this.loadMeeting();
    },
    beforeRouteLeave(to, from, next) {
      this.stopLocalStream();
      next();
    }
  }
</script>

<style src="@/assets/css/online-meeting.css"></style>
